/**
 * Airline Sizing Chart dialog — fork of product compare dialog.
 * PC: each product spans 2 columns (Carbin Size / Personal Item).
 * Mobile: each airline is a 3-row group.
 */
(() => {
  const IN_TO_CM = 2.54;
  const PASS = '√';
  const FAIL = '-';
  const useComma = String(Shopify.locale || '').toLowerCase().startsWith('de');

  /**
   * Parse a size number. English `.` always; German `,` only when useComma.
   * @param {string} value
   * @returns {number}
   */
  function parseSizeNumber(value) {
    const text = String(value ?? '').trim();
    if (!text) return NaN;
    if (!useComma) {
      return parseFloat(text);
    }
    const lastComma = text.lastIndexOf(',');
    const lastDot = text.lastIndexOf('.');
    if (lastComma > lastDot) {
      // 20,5 or 1.234,5
      return parseFloat(text.replace(/\./g, '').replace(',', '.'));
    }
    // 20.5 or 1,234.5 or digits only
    return parseFloat(text.replace(/,/g, ''));
  }

  /**
   * @param {string} sizeText
   * @returns {{ dims: number[]; unit: 'cm' | 'in' } | null}
   */
  function parseSizeDims(sizeText) {
    const text = String(sizeText || '').trim();
    if (!text || text === '—') return null;

    // Supports:
    // - 31 x 20.5 x 42 cm
    // - 31 cm x 20.5 cm x 42 cm
    // - 34 cm × 20 cm × 49 cm
    // - 22" x 14" x 9"
    // With useComma: 31 x 20,5 x 42 cm / 31 cm x 20,5 cm x 42 cm
    const number = useComma ? '(\\d+[.,]?\\d*)' : '(\\d+\\.?\\d*)';
    const multi = text.match(
      new RegExp(
        `${number}\\s*(in|cm|["″])?\\s*[x×]\\s*${number}\\s*(in|cm|["″])?\\s*[x×]\\s*${number}\\s*(in|cm|["″])?`,
        'i',
      ),
    );
    if (multi) {
      const dims = [parseSizeNumber(multi[1]), parseSizeNumber(multi[3]), parseSizeNumber(multi[5])];
      if (dims.some((n) => !Number.isFinite(n))) return null;

      let unit = 'cm';
      const unitToken = multi[6] || multi[4] || multi[2];
      if (unitToken) {
        const u = unitToken.toLowerCase();
        if (u === 'in' || u === '"' || u === '″') unit = 'in';
      } else if (/["″]\s*$/.test(text) || /\bin\b/i.test(text)) {
        unit = 'in';
      }

      return {dims, unit};
    }

    return null;
  }

  /**
   * @param {{ length?: unknown; width?: unknown; depth?: unknown }} limit
   * @returns {number[] | null}
   */
  function limitDimsCm(limit) {
    if (!limit || typeof limit !== 'object') return null;
    const dims = [Number(limit.length), Number(limit.width), Number(limit.depth)];
    if (dims.some((n) => !Number.isFinite(n))) return null;
    return dims;
  }

  /**
   * @param {Record<string, any> | null | undefined} limit
   * @returns {'dimensions' | 'linear'}
   */
  function getSizeRule(limit) {
    const rule = String(limit?.rule || limit?.ruleType || '')
      .trim()
      .toLowerCase();
    if (rule === 'linear' || rule === 'linear_sum' || rule === 'total' || rule === 'sum') {
      return 'linear';
    }
    return 'dimensions';
  }

  /**
   * @param {number[]} airlineCm
   * @returns {number[]}
   */
  function airlineLimitInches(airlineCm) {
    return airlineCm.map((cm) => Math.round(cm / IN_TO_CM));
  }

  /**
   * @param {{ dims: number[]; unit: 'cm' | 'in' } | null} productSize
   * @returns {number[] | null}
   */
  function productDimsCm(productSize) {
    if (!productSize?.dims?.length) return null;
    if (productSize.unit === 'in') {
      return productSize.dims.map((dim) => dim * IN_TO_CM);
    }
    return productSize.dims;
  }

  /**
   * @param {{ dims: number[]; unit: 'cm' | 'in' } | null} productSize
   * @param {{
   *   rule?: unknown;
   *   ruleType?: unknown;
   *   length?: unknown;
   *   width?: unknown;
   *   depth?: unknown;
   *   maxLinear?: unknown;
   *   linearMax?: unknown;
   *   maxSum?: unknown;
   * } | null | undefined} limit
   * @returns {boolean}
   */
  function isWithinLimit(productSize, limit) {
    if (!productSize) return false;
    const rule = getSizeRule(limit);

    if (rule === 'linear') {
      const maxLinearCm = Number(limit?.maxLinear ?? limit?.linearMax ?? limit?.maxSum);
      if (!Number.isFinite(maxLinearCm)) return false;
      const dimsCm = productDimsCm(productSize);
      if (!dimsCm || dimsCm.length < 3) return false;
      const sumCm = dimsCm[0] + dimsCm[1] + dimsCm[2];
      return sumCm <= maxLinearCm;
    }

    const airlineCm = limitDimsCm(limit || {});
    if (!airlineCm) return false;

    const limits =
      productSize.unit === 'in' ? airlineLimitInches(airlineCm) : airlineCm;

    // Orientation-agnostic: bags can be rotated, so compare sorted triples.
    const productSorted = [...productSize.dims].sort((a, b) => a - b);
    const limitSorted = [...limits].sort((a, b) => a - b);
    return productSorted.every((dim, index) => dim <= limitSorted[index]);
  }

  /**
   * @param {unknown} raw
   * @returns {Array<Record<string, any>>}
   */
  function normalizeAirlines(raw) {
    if (!raw) return [];
    let value = raw;
    if (typeof value === 'string') {
      try {
        value = JSON.parse(value);
      } catch {
        return [];
      }
    }
    const list = Array.isArray(value)
      ? value
      : Array.isArray(value?.airlines)
        ? value.airlines
        : [];
    return list.filter((item) => item && typeof item === 'object' && item.name);
  }

  /**
   * Resolve whether Personal Item should be hidden for the current dialog context.
   * Order (future-ready):
   * 1. Per-product override (product.hidePersonalItem) — reserved for specs metafield
   * 2. Product-type setting from chart productTypes[]
   * 3. data-hide-personal-item on the dialog host
   * 4. false
   *
   * @param {{
   *   productType?: string | null;
   *   productTypes?: Array<{ type?: string; hidePersonalItem?: boolean }> | null;
   *   hostHidePersonalItem?: boolean | null;
   *   productHidePersonalItem?: boolean | null;
   * }} input
   * @returns {boolean}
   */
  function resolveHidePersonalItem(input = {}) {
    if (input.productHidePersonalItem != null) {
      return Boolean(input.productHidePersonalItem);
    }

    const productType = String(input.productType || '').trim();
    const productTypes = Array.isArray(input.productTypes) ? input.productTypes : [];
    if (productType && productTypes.length) {
      const matched = productTypes.find(
        (entry) => entry && String(entry.type || '').trim() === productType,
      );
      if (matched) return Boolean(matched.hidePersonalItem);
    }

    if (input.hostHidePersonalItem != null) {
      return Boolean(input.hostHidePersonalItem);
    }

    return false;
  }

  if (!customElements.get('product-airline-sizing-dialog')) {
    customElements.define(
      'product-airline-sizing-dialog',
      class ProductAirlineSizingDialog extends window.theme.modules.CommonPopup {
        constructor() {
          super();

          this.productType = this.getAttribute('data-product-type') || '';
          this.mainProductHandle = this.getAttribute('data-main-product-handle') || '';
          this.mainProductTitle = this.getAttribute('data-main-product-title') || '';
          this.maxCompareSize = this.parseMaxCompareSize(
            this.getAttribute('data-max-compare-size'),
          );

          this.renderElement = this.querySelector('[data-airline-dialog-content-render]');
          this.ajaxContent = this.querySelector('[data-airline-dialog-ajax-content]');
          this.retryButton = this.querySelector('[data-airline-dialog-ajax-retry]');

          this._reload = this.reload.bind(this);
          this._onSelectionChange = this.onSelectionChange.bind(this);
          this._onStickyCheck = this.checkStickyState.bind(this);
          this._onTableWrapScroll = this.onTableWrapScroll.bind(this);
          this._onEqualizeCompareCards = this.equalizeCompareCardHeights.bind(this);
          this._onUnitSwitchClick = this.onUnitSwitchClick.bind(this);

          this.initialized = false;
          this.compareMeta = {
            size_map: {},
            airlines: [],
            translations: {},
            default_unit: 'imperial',
          };
          this.productTemplates = new Map();
          this.cachedResponseText = new Map();
          this.cachedProducts = new Map();
          this.currentUnit = null;
          this.tableDragScroll = null;
          this._loadedProductType = null;
          this._boundMainHandle = null;
          this._reloadId = 0;
          this._openId = 0;
        }

        get loadingState() {
          return this.getAttribute('data-loading-state');
        }

        set loadingState(state) {
          this.setAttribute('data-loading-state', state);
        }

        parseMaxCompareSize(value, fallback = 4) {
          if (value == null || value === '') return fallback;
          const raw = String(value).trim();
          if (/^infinity$/i.test(raw)) return Infinity;
          const num = Number(raw);
          return Number.isFinite(num) && num > 0 ? num : fallback;
        }

        connectedCallback() {
          super.connectedCallback();
          this.retryButton?.addEventListener('click', this._reload);
        }

        disconnectedCallback() {
          super.disconnectedCallback();
          this.retryButton?.removeEventListener('click', this._reload);
          this.selector?.removeEventListener(
            'theme:product-compare:selection-change',
            this._onSelectionChange,
          );
          this.teardownStickyObserver();
          this.teardownTableDragScroll();
          this.teardownCompareCardHeightSync();
        }

        /**
         * 打开弹窗。传入 handle 时复用同一个弹窗加载对应产品。
         * @param {string} [productHandle]
         */
        open(productHandle) {
          super.open();
          this.setAttribute('open', 'true');
          document.dispatchEvent(
            new CustomEvent('theme:scroll:lock', {
              bubbles: true,
              detail: this.renderElement,
            }),
          );
          this.prepareAndLoad(productHandle);
        }

        /**
         * @param {string} [productHandle]
         */
        async prepareAndLoad(productHandle) {
          const token = ++this._openId;
          try {
            if (productHandle) {
              await this.applyMainProduct(productHandle);
            }
            if (token !== this._openId) return;
            if (!this.productType || !this.mainProductHandle) {
              throw new Error('ProductAirlineSizingDialog: Missing product handle or type');
            }

            const typeChanged = this._loadedProductType !== this.productType;
            if (!this.initialized || typeChanged) {
              await this.reload();
              return;
            }

            if (this._boundMainHandle !== this.mainProductHandle) {
              this.bindContent();
            }
          } catch (error) {
            console.error('[ProductAirlineSizingDialog]', error);
            this.showErrorDetail(error);
            this.loadingState = 'error';
          }
        }

        /**
         * @param {string} handle
         */
        async applyMainProduct(handle) {
          const product = await this.fetchProductJson(handle);
          this.mainProductHandle = product.handle || handle;
          this.mainProductTitle = product.title || handle;
          this.productType = product.product_type || product.type || '';
          this.setAttribute('data-main-product-handle', this.mainProductHandle);
          this.setAttribute('data-main-product-title', this.mainProductTitle);
          this.setAttribute('data-product-type', this.productType);
        }

        /**
         * @param {string} handle
         */
        async fetchProductJson(handle) {
          if (this.cachedProducts.has(handle)) {
            return this.cachedProducts.get(handle);
          }
          const response = await fetch(`${window.theme.routes.root_url}products/${handle}.js`);
          if (!response.ok) {
            const error = new Error(`Product fetch failed: ${response.status}`);
            error.status = response.status;
            throw error;
          }
          const json = await response.json();
          this.cachedProducts.set(handle, json);
          return json;
        }

        async reload() {
          if (!this.productType) return;
          const token = ++this._reloadId;
          this.loadingState = 'loading';
          this.clearErrorDetail();

          try {
            await this.fetchContent();
            if (token !== this._reloadId) return;
            this.loadingState = 'success';
            this.initialized = true;
            this._loadedProductType = this.productType;
            this.bindContent();
          } catch (error) {
            if (token !== this._reloadId) return;
            this.cachedResponseText.delete(this.productType);
            console.error('[ProductAirlineSizingDialog]', error);
            this.showErrorDetail(error);
            this.loadingState = 'error';
          }
        }

        async fetchContent() {
          const productType = this.productType || '';
          let html = this.cachedResponseText.get(productType);

          if (!html) {
            const url =
              window.Shopify.routes.root +
              `collections/all-products?filter.p.product_type=${encodeURIComponent(productType)}&section_id=api-product-airline-sizing-dialog-content`;

            const response = await fetch(url);
            if (!response.ok) {
              const error = new Error(`Airline sizing dialog fetch failed: ${response.status}`);
              error.status = response.status;
              throw error;
            }
            html = await response.text();
            this.cachedResponseText.set(productType, html);
          }

          const fresh = document.createElement('div');
          fresh.innerHTML = html;
          const fetchContent = fresh.querySelector('[data-airline-dialog-ajax-content]');
          this.ajaxContent = this.ajaxContent || this.querySelector('[data-airline-dialog-ajax-content]');
          if (!fetchContent || !this.ajaxContent) {
            throw new Error('ProductAirlineSizingDialog: Ajax api not found content');
          }

          this.teardownStickyObserver();
          this.teardownTableDragScroll();
          this.teardownCompareCardHeightSync();
          this.selector?.removeEventListener(
            'theme:product-compare:selection-change',
            this._onSelectionChange,
          );
          this.ajaxContent.innerHTML = fetchContent.innerHTML;
        }

        close() {
          super.close();
          document.dispatchEvent(
            new CustomEvent('theme:scroll:unlock', {
              bubbles: true,
              detail: this.renderElement,
            }),
          );
          this.removeAttribute('open');
        }

        clearErrorDetail() {
          const detailEl = this.querySelector('[data-airline-dialog-error-detail]');
          if (!detailEl) return;
          detailEl.textContent = '';
          detailEl.classList.add('hide');
        }

        showErrorDetail(error) {
          const detailEl = this.querySelector('[data-airline-dialog-error-detail]');
          if (!detailEl) return;
          const status = error?.status;
          const detail =
            (typeof window.theme?.methods?.handleFetchErrorMessage === 'function'
              ? window.theme.methods.handleFetchErrorMessage(status)
              : '') || '';
          if (detail) {
            detailEl.textContent = detail;
            detailEl.classList.remove('hide');
          } else {
            detailEl.textContent = '';
            detailEl.classList.add('hide');
          }
        }

        bindContent() {
          this.selector = this.querySelector('product-compare-selector');
          this.tableContainer = this.querySelector('[data-airline-table-container]');
          this.tableWrap = this.querySelector('[data-airline-table-wrap]');
          this.table = this.querySelector('[data-airline-table]');
          this.theadRow = this.querySelector('[data-airline-product-row]');
          this.tbody = this.querySelector('[data-airline-tbody]');
          this.stickyWrapper = this.querySelector('[data-airline-sticky]');
          this.stickyInner = this.querySelector('[data-airline-sticky-inner]');
          this.stickyScroll = this.querySelector('[data-airline-sticky-scroll]');
          this.stickyRow = this.querySelector('[data-airline-sticky-row]');

          const metaEl = this.querySelector('[data-airline-meta]');
          try {
            this.compareMeta = JSON.parse(metaEl?.textContent || '{}');
          } catch (error) {
            console.error('[ProductAirlineSizingDialog] meta parse error', error);
            this.compareMeta = {size_map: {}, airlines: [], translations: {}};
          }
          this.compareMeta.airlines = normalizeAirlines(this.compareMeta.airlines);
          this.syncHidePersonalItemAttr();

          this.productTemplates = new Map();
          this.querySelectorAll('[data-airline-product-template]').forEach((template) => {
            const handle = template.getAttribute('data-airline-product-template');
            if (handle) this.productTemplates.set(handle, template);
          });

          const allHandles = [...this.productTemplates.keys()];
          const otherHandle = allHandles.find((handle) => handle !== this.mainProductHandle);
          const initialHandles = [this.mainProductHandle, otherHandle].filter(Boolean);

          this.setupStickyObserver();
          this.setupTableDragScroll();
          this.setupCompareCardHeightSync();

          this.selector?.removeEventListener(
            'theme:product-compare:selection-change',
            this._onSelectionChange,
          );
          if (this.selector) {
            this.selector.addEventListener(
              'theme:product-compare:selection-change',
              this._onSelectionChange,
            );
            this.selector.init({
              mainProductHandle: this.mainProductHandle,
              mainProductTitle: this.mainProductTitle,
              selectedHandles: initialHandles,
              maxCompareSize: this.maxCompareSize,
            });
          } else {
            this.renderCompareTable(initialHandles);
          }
          this._boundMainHandle = this.mainProductHandle;
        }

        setupStickyObserver() {
          this.teardownStickyObserver();
          if (!this.renderElement || !this.theadRow || !this.stickyWrapper) return;
          this.renderElement.addEventListener('scroll', this._onStickyCheck, {passive: true});
          this.tableWrap?.addEventListener('scroll', this._onTableWrapScroll, {passive: true});
          window.addEventListener('resize', this._onStickyCheck);
          this.checkStickyState();
        }

        teardownStickyObserver() {
          this.renderElement?.removeEventListener('scroll', this._onStickyCheck);
          this.tableWrap?.removeEventListener('scroll', this._onTableWrapScroll);
          window.removeEventListener('resize', this._onStickyCheck);
          this.stickyInner?.classList.remove('is-active');
          if (this.stickyWrapper) this.stickyWrapper.style.top = '';
        }

        setupTableDragScroll() {
          this.teardownTableDragScroll();
          const DragSnapScroll = window.theme?.modules?.DragSnapScroll;
          if (!this.tableWrap || !DragSnapScroll) return;
          this.tableDragScroll = new DragSnapScroll(this.tableWrap, {
            getSnapStep: () => this.getTableScrollStep(),
          });
          this.tableDragScroll.bind();
        }

        teardownTableDragScroll() {
          this.tableDragScroll?.destroy();
          this.tableDragScroll = null;
        }

        getTableScrollStep() {
          if (!this.tableWrap) return 200;
          const cell = this.tableWrap.querySelector(
            '.product-compare-dialog__product, .product-airline-sizing-dialog__subcol',
          );
          const width = cell?.offsetWidth || 0;
          return width * 2 || Math.max(this.tableWrap.clientWidth * 0.8, 200);
        }

        onTableWrapScroll() {
          this.syncStickyScrollFromTable();
        }

        setupCompareCardHeightSync() {
          this.teardownCompareCardHeightSync();
          this._onEqualizeCompareCards = this.equalizeCompareCardHeights.bind(this);
          window.addEventListener('resize', this._onEqualizeCompareCards);
          this.scheduleEqualizeCompareCardHeights();
        }

        teardownCompareCardHeightSync() {
          window.removeEventListener('resize', this._onEqualizeCompareCards);
        }

        scheduleEqualizeCompareCardHeights() {
          requestAnimationFrame(() => {
            requestAnimationFrame(() => this.equalizeCompareCardHeights());
          });
        }

        equalizeCompareCardHeights() {
          if (!this.theadRow) return;
          const cards = [...this.theadRow.querySelectorAll('[data-compare-card]')];
          if (!cards.length) return;
          cards.forEach((card) => {
            card.style.minHeight = '';
          });
          let max = 0;
          cards.forEach((card) => {
            max = Math.max(max, card.offsetHeight);
          });
          if (max > 0) {
            cards.forEach((card) => {
              card.style.minHeight = `${max}px`;
            });
          }
        }

        checkStickyState() {
          if (!this.theadRow || !this.stickyInner || !this.renderElement) return;
          const renderRect = this.renderElement.getBoundingClientRect();
          const headRect = this.theadRow.getBoundingClientRect();
          const shouldStick = headRect.bottom <= renderRect.top + 1;
          if (this.stickyWrapper) {
            this.stickyWrapper.style.top = `${Math.max(0, renderRect.top)}px`;
          }
          this.stickyInner.classList.toggle('is-active', shouldStick);
        }

        syncStickyScrollFromTable() {
          if (!this.tableWrap || !this.stickyScroll) return;
          this.stickyScroll.scrollLeft = this.tableWrap.scrollLeft;
        }

        renderStickyTitles(products) {
          if (!this.stickyRow) return;
          this.stickyRow.innerHTML = '';
          const corner = document.createElement('div');
          corner.className = 'product-compare-dialog__sticky-corner';
          corner.setAttribute('aria-hidden', 'true');
          this.stickyRow.appendChild(corner);

          products.forEach((product, index) => {
            const cell = document.createElement('div');
            cell.className =
              'product-compare-dialog__sticky-title product-airline-sizing-dialog__sticky-title';
            cell.dataset.productHandle = product.handle;
            if (index === 0) cell.classList.add('product-compare-dialog__sticky-title--first');
            cell.textContent = product.title;
            this.stickyRow.appendChild(cell);
          });

          this.tableContainer?.style.setProperty('--columns', String(products.length));
          this.tableContainer?.setAttribute('data-columns', String(products.length));
          this.syncStickyScrollFromTable();
          requestAnimationFrame(() => this.checkStickyState());
        }

        onSelectionChange(e) {
          const selectedHandles = e.detail?.selectedHandles || [];
          this.renderCompareTable(selectedHandles);
        }

        /**
         * Dialog-level Personal Item visibility (same product type for all compared products).
         * Per-product override can later plug into resolveHidePersonalItem via product.hidePersonalItem.
         * @returns {boolean}
         */
        getHidePersonalItem() {
          return resolveHidePersonalItem({
            productType: this.productType,
            productTypes: this.compareMeta?.product_types,
            hostHidePersonalItem: this.getAttribute('data-hide-personal-item') === 'true',
          });
        }

        /**
         * Table grid always uses 2 columns per product so mobile label|result layout stays intact.
         * When Personal Item is hidden, desktop cabin cells use colSpan=2 instead of dropping a column.
         * @returns {2}
         */
        getProductColSpan() {
          return 2;
        }

        syncHidePersonalItemAttr() {
          const hide = this.getHidePersonalItem();
          this.setAttribute('data-hide-personal-item', hide ? 'true' : 'false');
          this.table?.setAttribute('data-hide-personal-item', hide ? 'true' : 'false');
          this.tableContainer?.setAttribute('data-hide-personal-item', hide ? 'true' : 'false');
        }

        /**
         * @param {string[]} selectedHandles
         */
        renderCompareTable(selectedHandles) {
          if (!this.theadRow || !this.tbody) return;

          const products = selectedHandles
            .map((handle) => this.getProductData(handle))
            .filter(Boolean);

          this.syncHidePersonalItemAttr();
          this.reconcileProductCards(products);
          this.tbody.innerHTML = '';

          const translations = this.compareMeta.translations || {};
          const sizeLabel = this.getSizeFieldLabel();
          const productCount = products.length;
          const airlines = this.compareMeta.airlines || [];
          const hidePersonalItem = this.getHidePersonalItem();
          const colSpan = this.getProductColSpan();

          this.tbody.appendChild(this.buildUnitSwitchRow(productCount, colSpan));
          this.tbody.appendChild(this.buildSizeRow(sizeLabel, products, colSpan));
          this.tbody.appendChild(this.buildSpacerRow(productCount, colSpan));

          // PC sub-header (hidden/restructured on mobile via CSS)
          this.tbody.appendChild(
            this.buildAirlineSubHeaderRow(productCount, translations, hidePersonalItem),
          );

          airlines.forEach((airline) => {
            this.tbody.appendChild(
              this.buildAirlineGroupRows(airline, products, translations, hidePersonalItem),
            );
          });

          this.table?.style.setProperty('--columns', String(products.length));
          this.table?.setAttribute('data-columns', String(products.length));
          this.renderStickyTitles(products);
          this.applyUnit(this.getActiveUnit());
          this.scheduleEqualizeCompareCardHeights();
        }

        getSizeFieldLabel() {
          const sizeMap = this.compareMeta.size_map || {};
          const groups = this.normalizeSizeMapGroups(sizeMap);
          for (const group of groups) {
            if (group.size) return group.size;
          }
          if (sizeMap.size && typeof sizeMap.size === 'string') return sizeMap.size;
          return this.compareMeta.translations?.size || 'Size';
        }

        normalizeSizeMapGroups(sizeMap) {
          if (!sizeMap || typeof sizeMap !== 'object') return [];
          if (Array.isArray(sizeMap)) {
            return sizeMap.filter((g) => g && typeof g === 'object' && !Array.isArray(g));
          }
          if (Array.isArray(sizeMap.groups)) {
            return sizeMap.groups.filter((g) => g && typeof g === 'object' && !Array.isArray(g));
          }
          /** @type {Record<string, string>} */
          const flat = {};
          Object.entries(sizeMap).forEach(([key, value]) => {
            if (typeof value === 'string') flat[key] = value;
          });
          return Object.keys(flat).length ? [flat] : [];
        }

        reconcileProductCards(products) {
          if (!this.theadRow) return;

          const desiredHandles = products.map((product) => product.handle);
          const existingByHandle = new Map();
          this.theadRow
            .querySelectorAll('th.product-compare-dialog__product[data-product-handle]')
            .forEach((th) => {
              existingByHandle.set(th.dataset.productHandle, th);
            });

          existingByHandle.forEach((th, handle) => {
            if (!desiredHandles.includes(handle)) {
              th.remove();
              existingByHandle.delete(handle);
            }
          });

          let corner = this.theadRow.querySelector('th.product-compare-dialog__corner');
          if (!corner) {
            corner = document.createElement('th');
            corner.className = 'product-compare-dialog__corner';
            corner.scope = 'col';
            this.theadRow.prepend(corner);
          } else if (this.theadRow.firstElementChild !== corner) {
            this.theadRow.prepend(corner);
          }

          products.forEach((product, index) => {
            const targetIndex = index + 1;
            let th = existingByHandle.get(product.handle);
            const isNew = !th;
            const colSpan = this.getProductColSpan();

            if (!th) {
              th = document.createElement('th');
              th.className = 'product-compare-dialog__product';
              th.scope = 'col';
              th.colSpan = colSpan;
              th.dataset.productHandle = product.handle;
              const card = product.cardNode.cloneNode(true);
              card.querySelectorAll('img').forEach((img) => {
                img.loading = 'eager';
              });
              th.appendChild(card);
            } else {
              th.colSpan = colSpan;
            }

            const currentChild = this.theadRow.children[targetIndex];
            if (currentChild !== th) {
              if (currentChild) this.theadRow.insertBefore(th, currentChild);
              else this.theadRow.appendChild(th);
            }

            if (isNew) {
              th.querySelectorAll('product-grid-item-v3').forEach((el) => {
                if (typeof el.connectedCallback === 'function') el.connectedCallback();
              });
            }
          });
        }

        getProductData(handle) {
          const template = this.productTemplates.get(handle);
          if (!template) return null;
          const root = template.content.querySelector('[data-airline-product]');
          if (!root) return null;

          let size = {};
          try {
            size = JSON.parse(root.getAttribute('data-specs-size') || '{}') || {};
          } catch (_) {
            size = {};
          }

          const cardNode = root.querySelector('[data-compare-card]');
          const sizeText = size?.size != null ? String(size.size) : '';
          const parsedSize = parseSizeDims(sizeText);

          return {
            handle,
            title: root.getAttribute('data-product-title') || handle,
            size,
            sizeText,
            parsedSize,
            cardNode,
          };
        }

        getActiveUnit() {
          return (
            this.currentUnit ||
            this.compareMeta?.default_unit ||
            window.theme?.settings?.product_default_unit ||
            'imperial'
          );
        }

        buildUnitSwitchRow(productCount, colSpan = 2) {
          const translations = this.compareMeta.translations || {};
          const defaultUnit = this.getActiveUnit();
          const imperialLabel = translations.imperial || 'Imperial';
          const metricLabel = translations.metric || 'Metric';

          const createSwitch = (modifierClass) => {
            const wrap = document.createElement('div');
            wrap.className = `product-compare-dialog__unit-switch product-specifications__header-buttons ${modifierClass}`;
            wrap.setAttribute('data-default-unit', defaultUnit);
            const buttonOrder =
              defaultUnit === 'metric'
                ? [
                    {unit: 'metric', label: metricLabel},
                    {unit: 'imperial', label: imperialLabel},
                  ]
                : [
                    {unit: 'imperial', label: imperialLabel},
                    {unit: 'metric', label: metricLabel},
                  ];
            buttonOrder.forEach(({unit, label}) => {
              const button = document.createElement('button');
              button.type = 'button';
              button.className = `link-underline-hover link-underline${unit === defaultUnit ? ' active' : ''}`;
              button.setAttribute('data-unit-switch', unit);
              button.textContent = label;
              button.addEventListener('click', this._onUnitSwitchClick);
              wrap.appendChild(button);
            });
            return wrap;
          };

          const tr = document.createElement('tr');
          tr.className = 'product-compare-dialog__unit-row';

          const paramCell = document.createElement('th');
          paramCell.scope = 'row';
          paramCell.className = 'product-compare-dialog__param product-compare-dialog__unit-cell';
          paramCell.appendChild(createSwitch('product-compare-dialog__unit-switch--desktop'));
          tr.appendChild(paramCell);

          for (let i = 0; i < productCount; i += 1) {
            const td = document.createElement('td');
            td.className = 'product-compare-dialog__value product-compare-dialog__unit-cell';
            td.colSpan = colSpan;
            if (i === 0) {
              td.appendChild(createSwitch('product-compare-dialog__unit-switch--mobile'));
            }
            tr.appendChild(td);
          }

          return tr;
        }

        onUnitSwitchClick(e) {
          const button = e.currentTarget;
          const unit = button?.getAttribute('data-unit-switch');
          if (!unit) return;
          this.applyUnit(unit);
        }

        applyUnit(unit) {
          if (!unit || !this.tbody) return;
          this.currentUnit = unit;
          this.tbody.querySelectorAll('[data-unit-switch]').forEach((button) => {
            button.classList.toggle('active', button.getAttribute('data-unit-switch') === unit);
          });
          const convertUnit = window.theme?.methods?.convertUnit;
          this.tbody.querySelectorAll('[data-compare-convertible]').forEach((el) => {
            const original = el.getAttribute('data-original-html');
            if (original == null) return;
            if (typeof convertUnit === 'function') {
              el.innerHTML = convertUnit(original, unit);
            } else {
              el.innerHTML = original;
            }
          });
        }

        buildSizeRow(label, products, colSpan = 2) {
          const displayLabel = this.formatParamLabel(label);
          const tr = document.createElement('tr');
          tr.className = 'product-airline-sizing-dialog__size-row';

          const th = document.createElement('th');
          th.scope = 'row';
          th.className = 'product-compare-dialog__param';
          const thLabel = document.createElement('span');
          thLabel.className = 'product-compare-dialog__param-label';
          thLabel.textContent = displayLabel;
          th.appendChild(thLabel);
          tr.appendChild(th);

          products.forEach((product) => {
            const td = document.createElement('td');
            td.className = 'product-compare-dialog__value';
            td.colSpan = colSpan;

            const cellLabel = document.createElement('span');
            cellLabel.className =
              'product-compare-dialog__param-label product-compare-dialog__param-label--cell';
            cellLabel.textContent = displayLabel;
            td.appendChild(cellLabel);

            const valueEl = document.createElement('span');
            valueEl.className = 'product-compare-dialog__param-value';
            const value = product.sizeText;
            const hasValue = value != null && String(value).trim() !== '';
            valueEl.textContent = hasValue ? String(value) : '—';
            valueEl.setAttribute('data-compare-convertible', '');
            valueEl.setAttribute('data-original-html', valueEl.innerHTML);
            td.appendChild(valueEl);
            tr.appendChild(td);
          });

          return tr;
        }

        buildSpacerRow(productCount, colSpan = 2) {
          const tr = document.createElement('tr');
          tr.className = 'product-compare-dialog__spacer-row';

          const th = document.createElement('th');
          th.scope = 'row';
          th.className = 'product-compare-dialog__param product-compare-dialog__spacer-cell';
          th.setAttribute('aria-hidden', 'true');
          tr.appendChild(th);

          const td = document.createElement('td');
          td.className = 'product-compare-dialog__value product-compare-dialog__spacer-cell';
          td.colSpan = Math.max(productCount * colSpan, 1);
          td.setAttribute('aria-hidden', 'true');
          tr.appendChild(td);

          return tr;
        }

        buildAirlineSubHeaderRow(productCount, translations, hidePersonalItem = false) {
          const tr = document.createElement('tr');
          tr.className = 'product-airline-sizing-dialog__subheader-row';

          const th = document.createElement('th');
          th.scope = 'row';
          th.className = 'product-compare-dialog__param';
          const thLabel = document.createElement('span');
          thLabel.className = 'product-compare-dialog__param-label';
          thLabel.textContent = translations.airline || 'Airline';
          th.appendChild(thLabel);
          tr.appendChild(th);

          for (let i = 0; i < productCount; i += 1) {
            const cabinTd = document.createElement('td');
            cabinTd.className =
              'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--cabin';
            if (hidePersonalItem) {
              cabinTd.colSpan = 2;
              cabinTd.classList.add('product-airline-sizing-dialog__subcol--solo');
            }
            cabinTd.textContent = translations.carbin_size || 'Carbin Size';
            tr.appendChild(cabinTd);

            if (!hidePersonalItem) {
              const personalTd = document.createElement('td');
              personalTd.className =
                'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--personal';
              personalTd.textContent = translations.personal_item || 'Personal Item';
              tr.appendChild(personalTd);
            }
          }

          return tr;
        }

        /**
         * @param {Record<string, any>} airline
         * @param {Array<object>} products
         * @param {Record<string, string>} translations
         * @param {boolean} [hidePersonalItem]
         */
        buildAirlineGroupRows(airline, products, translations, hidePersonalItem = false) {
          const fragment = document.createDocumentFragment();
          const cabinLabel = translations.carbin_size || 'Carbin Size';
          const personalLabel = translations.personal_item || 'Personal Item';

          // Desktop: single row with airline name + pass/fail cells
          const desktopTr = document.createElement('tr');
          desktopTr.className = 'product-airline-sizing-dialog__airline-row product-airline-sizing-dialog__airline-row--desktop';

          const nameTh = document.createElement('th');
          nameTh.scope = 'row';
          nameTh.className = 'product-compare-dialog__param product-airline-sizing-dialog__airline-name';
          nameTh.appendChild(this.buildAirlineNameContent(airline));
          desktopTr.appendChild(nameTh);

          products.forEach((product) => {
            const cabinOk = isWithinLimit(product.parsedSize, airline.carryOn);

            const cabinTd = document.createElement('td');
            cabinTd.className =
              'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--cabin product-airline-sizing-dialog__result';
            if (hidePersonalItem) {
              cabinTd.colSpan = 2;
              cabinTd.classList.add('product-airline-sizing-dialog__subcol--solo');
            }
            cabinTd.textContent = cabinOk ? PASS : FAIL;
            desktopTr.appendChild(cabinTd);

            if (!hidePersonalItem) {
              const personalOk = isWithinLimit(product.parsedSize, airline.personalItem);
              const personalTd = document.createElement('td');
              personalTd.className =
                'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--personal product-airline-sizing-dialog__result';
              personalTd.textContent = personalOk ? PASS : FAIL;
              desktopTr.appendChild(personalTd);
            }
          });
          fragment.appendChild(desktopTr);

          // Mobile: per product — name + Carbin row (+ Personal row when visible)
          // Keep 2-column-per-product grid so label|result layout matches the non-hidden mode.
          const nameTr = document.createElement('tr');
          nameTr.className =
            'product-airline-sizing-dialog__airline-row product-airline-sizing-dialog__airline-row--mobile product-airline-sizing-dialog__airline-name-row';

          const nameParam = document.createElement('th');
          nameParam.scope = 'row';
          nameParam.className =
            'product-compare-dialog__param product-airline-sizing-dialog__airline-name';
          nameParam.appendChild(this.buildAirlineNameContent(airline));
          nameTr.appendChild(nameParam);

          products.forEach(() => {
            const nameTd = document.createElement('td');
            nameTd.colSpan = 2;
            nameTd.className =
              'product-compare-dialog__value product-airline-sizing-dialog__airline-name-cell';
            nameTd.appendChild(this.buildAirlineNameContent(airline));
            nameTr.appendChild(nameTd);
          });
          fragment.appendChild(nameTr);

          fragment.appendChild(
            this.buildMobileLimitRow(cabinLabel, products, airline, 'carryOn'),
          );
          if (!hidePersonalItem) {
            fragment.appendChild(
              this.buildMobileLimitRow(personalLabel, products, airline, 'personalItem'),
            );
          }

          return fragment;
        }

        buildAirlineNameContent(airline) {
          const wrap = document.createElement('span');
          wrap.className = 'product-airline-sizing-dialog__airline-name-inner';
          const text = document.createElement('span');
          text.textContent = String(airline.name || '');
          wrap.appendChild(text);
          return wrap;
        }

        /**
         * @param {string} label
         * @param {Array<object>} products
         * @param {Record<string, any>} airline
         * @param {'carryOn' | 'personalItem'} limitKey
         */
        buildMobileLimitRow(label, products, airline, limitKey) {
          const tr = document.createElement('tr');
          tr.className =
            'product-airline-sizing-dialog__airline-row product-airline-sizing-dialog__airline-row--mobile product-airline-sizing-dialog__limit-row';

          const th = document.createElement('th');
          th.scope = 'row';
          th.className = 'product-compare-dialog__param';
          const thLabel = document.createElement('span');
          thLabel.className = 'product-compare-dialog__param-label';
          thLabel.textContent = label;
          th.appendChild(thLabel);
          tr.appendChild(th);

          const displayLabel = this.formatParamLabel(label);

          products.forEach((product) => {
            const ok = isWithinLimit(product.parsedSize, airline[limitKey]);

            const labelTd = document.createElement('td');
            labelTd.className =
              'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--cabin product-airline-sizing-dialog__limit-label';
            labelTd.textContent = displayLabel;
            tr.appendChild(labelTd);

            const resultTd = document.createElement('td');
            resultTd.className =
              'product-compare-dialog__value product-airline-sizing-dialog__subcol product-airline-sizing-dialog__subcol--personal product-airline-sizing-dialog__result';
            resultTd.textContent = ok ? PASS : FAIL;
            tr.appendChild(resultTd);
          });

          return tr;
        }

        formatParamLabel(label) {
          const text = String(label || '').trim();
          if (!text) return '';
          return /[:：]$/.test(text) ? text : `${text}:`;
        }
      },
    );
  }

  // Expose helpers for manual checks
  window.theme = window.theme || {};
  window.theme.methods = window.theme.methods || {};
  window.theme.methods.airlineSizing = {
    parseSizeNumber,
    parseSizeDims,
    isWithinLimit,
    airlineLimitInches,
    resolveHidePersonalItem,
  };
})();
